/**
 * Which motion the connection dialog plays (Option C, C1b, plan §3.2 "Reduced motion" and "Tests and motion"):
 *
 * - `off` — `<html data-motion="off">` (the e2e fixture sets it on every document, `setup/appFixture.ts`), or a runtime
 *   without the Web Animations API (jsdom): views and panes swap instantly and `animate()` is never called;
 * - `reduced` — the OS asks for reduced motion: no movement, a 150 ms opacity cross-fade (D12, the prototype's choice);
 * - `full` — the shared-element FLIP, the pane slide and the rail highlight.
 *
 * It is read when a navigation starts, never cached: the attribute and the preference apply from the first transition
 * after they change (a preference change during a flight settles it, `useSharedLayoutTransition`).
 */
export type MotionMode = 'full' | 'reduced' | 'off';

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export type MotionEnvironment = {
  documentElement: Element | null;
  matchMedia?: (query: string) => { matches: boolean };
  /** `Element.prototype.animate` exists. */
  canAnimate: boolean;
};

export function browserMotionEnvironment(): MotionEnvironment {
  const hasWindow = typeof window !== 'undefined';
  return {
    documentElement: typeof document === 'undefined' ? null : document.documentElement,
    matchMedia: hasWindow && typeof window.matchMedia === 'function' ? (query) => window.matchMedia(query) : undefined,
    canAnimate: typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function',
  };
}

export function motionMode(environment: MotionEnvironment = browserMotionEnvironment()): MotionMode {
  if (!environment.canAnimate) return 'off';
  if (environment.documentElement?.getAttribute('data-motion') === 'off') return 'off';
  if (environment.matchMedia?.(REDUCED_MOTION_QUERY).matches) return 'reduced';
  return 'full';
}
